
import React, { useCallback, useEffect, useRef, useState } from 'react';
import './HiddenUserBanner.css';

const STORAGE_KEY = 'hidden-user-button-position';
const LOGO_HEIGHT_PX = 32;
const BUTTON_SIZE_PX = LOGO_HEIGHT_PX * 2; // twice as big as logo (logo is h-8 = 32px)

const defaultPosition = { x: 16, y: 16 }; // left side of header, aligned with padding

function getStoredPosition(): { x: number; y: number } {
  if (typeof window === 'undefined') return defaultPosition;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPosition;
    const parsed = JSON.parse(raw) as { x: number; y: number };
    if (typeof parsed.x === 'number' && typeof parsed.y === 'number') return parsed;
  } catch {
    // ignore
  }
  return defaultPosition;
}

function storePosition(x: number, y: number) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ x, y }));
  } catch {
    // ignore
  }
}

/**
 * Draggable button shown when a hidden user is logged in.
 * Placed on the left of the header area, twice the size of the logo; can be dragged so it doesn't cover content.
 */
const HiddenUserBanner: React.FC = () => {
  const [position, setPosition] = useState(getStoredPosition);
  const [showTooltip, setShowTooltip] = useState(false);
  const didDragRef = useRef(false);
  const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);

  const persistPosition = useCallback((x: number, y: number) => {
    setPosition({ x, y });
    storePosition(x, y);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      didDragRef.current = false;
      dragStartRef.current = {
        x: position.x,
        y: position.y,
        startX: e.clientX,
        startY: e.clientY,
      };
    },
    [position]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      const dx = e.clientX - start.startX;
      const dy = e.clientY - start.startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDragRef.current = true;
      const x = Math.max(0, start.x + dx);
      const y = Math.max(0, start.y + dy);
      setPosition({ x, y });
    };

    const onUp = (e: PointerEvent) => {
      const start = dragStartRef.current;
      dragStartRef.current = null;
      if (start) {
        const dx = e.clientX - start.startX;
        const dy = e.clientY - start.startY;
        const x = Math.max(0, start.x + dx);
        const y = Math.max(0, start.y + dy);
        persistPosition(x, y);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [persistPosition]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (didDragRef.current) {
      e.preventDefault();
      didDragRef.current = false;
      return;
    }
    setShowTooltip((prev) => !prev);
  }, []);

  const tooltipMessage =
    'Hidden Account: Your profile and content are not visible to other users.';

  return (
    <>
      <button
        type="button"
        className="hidden-user-button"
        style={{
          left: position.x,
          top: position.y,
          width: BUTTON_SIZE_PX,
          height: BUTTON_SIZE_PX,
        }}
        title={tooltipMessage}
        aria-label={tooltipMessage}
        role="alert"
        aria-live="polite"
        onPointerDown={handlePointerDown}
        onClick={handleClick}
      >
        <span className="hidden-user-button-icon" aria-hidden="true">
          ⚠
        </span>
      </button>
      {showTooltip && (
        <div
          className="hidden-user-button-tooltip"
          style={{
            left: position.x,
            top: position.y + BUTTON_SIZE_PX + 8,
          }}
          role="tooltip"
        >
          {tooltipMessage}
        </div>
      )}
    </>
  );
};

export default HiddenUserBanner;
